import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { ComparationService } from "../../services/ComparationService";
import { Comparation } from "../../types/Comparation";
import { PositionDTO } from "../../types/PositionDTO";
import { SectorOption } from "../../types/SectorOption";
import { useAlert } from "../contextProviders/AlertProvider";
import "./ModalCreatePosition.scss";

interface Props {
	comparation: Comparation | undefined;
	cnpj: string | undefined;
	sectors: SectorOption[];
	open: boolean;
	onClose: () => void;
	onCreated: (positionId: number, targetSectorId: number) => void;
}

const ModalCreatePosition: FC<Props> = (props: Props) => {
	const [sourceSector, setSourceSector] = useState<number | string>('');
	const [sourcePosition, setSourcePosition] = useState<number | string>('');
	const [targetSector, setTargetSector] = useState<number | string>('');
	const [positions, setPositions] = useState<PositionDTO[]>([]);
	const [loadingPositions, setLoadingPositions] = useState(false);
	const [saving, setSaving] = useState(false);
	const { showAlert, showError } = useAlert();
	const comparationService = new ComparationService();
	const newPositionName = props.comparation?.esocialValues?.position;
	const esocialCbo = (props.comparation?.esocialValues?.cbo || '').split(' - ')[0].replace(/\D/g, '');
	const positionSelectLabel = `Cargo de origem (com o CBO ${esocialCbo || '-'})`;

	useEffect(() => {
		if (props.open) {
			setSourceSector('');
			setSourcePosition('');
			setTargetSector('');
			setPositions([]);
		}
	}, [props.open]);

	const getPositions = async (sectorId: string) => {
		if (!props.comparation) {
			return;
		}
		setLoadingPositions(true);
		const response = await comparationService
			.getPositions(props.comparation.id, Number(sectorId), props.cnpj);
		setPositions(response && response.data ? response.data : []);
		setLoadingPositions(false);
	};

	const handleChangeSourceSector = (sectorId: string) => {
		setSourceSector(sectorId);
		setSourcePosition('');
		setPositions([]);
		getPositions(sectorId);
	};

	const create = async () => {
		if (!props.comparation || !props.cnpj) {
			return;
		}
		setSaving(true);
		const response = await comparationService.createPosition(props.comparation.id, {
			cnpj: props.cnpj,
			sourceSectorId: Number(sourceSector),
			sourcePositionId: Number(sourcePosition),
			targetSectorId: Number(targetSector)
		});
		setSaving(false);
		if (!response || response.status !== 200 || !response.data?.positionId) {
			showError(response?.data?.error);
			return;
		}
		showAlert(response.data.warning
			? `Cargo "${response.data.position}" criado. ${response.data.warning}`
			: `Cargo "${response.data.position}" criado no SGG com as avaliações de risco e recomendações de exame!`,
			response.data.warning ? 'warning' : 'success');
		props.onCreated(response.data.positionId, Number(targetSector));
	};

	const canCreate = !!sourceSector && !!sourcePosition && !!targetSector && !!newPositionName;

	return (
		<Dialog
			open={props.open}
			onClose={props.onClose}
			className="modal-create-position"
			fullWidth
			maxWidth="sm"
		>
			<DialogTitle>Criar cargo "{newPositionName || '-'}"</DialogTitle>
			<DialogContent>
				<Alert severity="info" variant="outlined" className="info-message">
					O cargo novo será criado com o nome que veio do eSocial, copiando as informações, a avaliação
					de riscos mais recente e as recomendações de exame do cargo escolhido abaixo.
				</Alert>
				<Box className="create-position-fields">
					<Typography variant="subtitle2">A partir de qual cargo você quer criar?</Typography>
					<FormControl fullWidth>
						<InputLabel>Setor do cargo de origem</InputLabel>
						<Select
							label="Setor do cargo de origem"
							id="source-sector"
							value={sourceSector}
							onChange={(e) => handleChangeSourceSector(String(e.target.value))}
						>
							{props.sectors.map(item => (
								<MenuItem key={item.id_setor} value={item.id_setor}>
									<Box className="sector-option">
										<span>{item.setor}</span>
										<span className={item.positionsWithCbo ? 'sector-count' : 'sector-count-empty'}>({item.positionsWithCbo} cargos com CBO atual)</span>
										{item.current && <span className="sector-current">(setor atual)</span>}
									</Box>
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl fullWidth>
						<InputLabel>{positionSelectLabel}</InputLabel>
						<Select
							label={positionSelectLabel}
							id="source-position"
							value={sourcePosition}
							onChange={(e) => setSourcePosition(e.target.value)}
							disabled={!sourceSector || loadingPositions}
						>
							{positions.map(item => (
								<MenuItem key={item.id_cargo} value={item.id_cargo}>
									{`${item.cargo} - ${item.CBO}`}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{loadingPositions && <CircularProgress size="3vh"/>}
					{!!sourceSector && !loadingPositions && !positions.length && <Alert severity="warning" variant="outlined">
						Este setor não possui cargos com o CBO do eSocial ({esocialCbo || '-'}) para usar como base.
					</Alert>}
					<Typography variant="subtitle2">Em qual setor o cargo novo ficará?</Typography>
					<FormControl fullWidth>
						<InputLabel>Setor do novo cargo</InputLabel>
						<Select
							label="Setor do novo cargo"
							id="target-sector"
							value={targetSector}
							onChange={(e) => setTargetSector(e.target.value)}
						>
							{props.sectors.map(item => (
								<MenuItem key={item.id_setor} value={item.id_setor}>
									<Box className="sector-option">
										<span>{item.setor}</span>
										<span className={item.positionsWithCbo ? 'sector-count' : 'sector-count-empty'}>({item.positionsWithCbo} cargos com CBO atual)</span>
										{item.current && <span className="sector-current">(setor atual)</span>}
									</Box>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.onClose}>Cancelar</Button>
				<Button
					variant="contained"
					color="success"
					onClick={create}
					disabled={!canCreate || saving}
				>
					{saving ? <CircularProgress size="3vh"/> : 'Criar cargo'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ModalCreatePosition;
